import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import toast from 'react-hot-toast';
import type { ApiError, ApiEnvelope, AccessTokenResponse } from '@types';
import { getAccessToken, setAccessToken, clearAccessToken } from './tokenStore';

// Base URL set to /api - Vite proxy forwards /api/* to backend.
// API modules use paths like `/v1/auth/login` (baseURL + path = /api/v1/auth/login).
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Custom header required on the refresh endpoint in addition to SameSite
// protections (F1.9). Also serves as a lightweight CSRF signal on all calls.
const REQUESTED_WITH = 'XMLHttpRequest';

// Serialize concurrent refreshes so a burst of 401s triggers exactly one
// refresh; queued requests retry once the new token lands.
let isRefreshing = false;
let authFailureHandled = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Optional hook AuthContext registers so a failed refresh clears app auth state.
let onAuthFailure: (() => void) | null = null;
export const setAuthFailureHandler = (fn: (() => void) | null) => {
  onAuthFailure = fn;
};

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  // Send the refresh cookie on same-origin API calls (F1.2).
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': REQUESTED_WITH,
  },
});

// Request interceptor — attach the in-memory access token (never storage).
axiosInstance.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

/**
 * Silent refresh (F1.2). Uses a bare axios call (no interceptors) that sends
 * the httpOnly refresh cookie via `withCredentials` plus the custom header
 * (F1.9). Returns the new access token, or null on failure.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const response = await axios.post<ApiEnvelope<AccessTokenResponse>>(
      `${API_BASE_URL}/v1/auth/refresh`,
      null,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': REQUESTED_WITH,
        },
      }
    );
    const token = response.data?.data?.access_token ?? null;
    if (token) {
      setAccessToken(token);
      return token;
    }
    return null;
  } catch {
    // Explicit null — the caller decides how to surface re-auth. This is NOT a
    // silent empty-data fallback: a null token forces the login redirect.
    return null;
  }
};

const handleAuthFailure = () => {
  if (authFailureHandled) return;
  authFailureHandled = true;
  clearAccessToken();
  onAuthFailure?.();
  toast.error('Session expired. Please login again.');
  setTimeout(() => {
    authFailureHandled = false;
  }, 1000);
};

// Response interceptor — unwrap `response.data`; refresh-and-retry once on 401.
axiosInstance.interceptors.response.use(
  response => response.data,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response) {
      const { status, data } = error.response;

      if (status === 401 && originalRequest) {
        if (originalRequest._retry) {
          handleAuthFailure();
          return Promise.reject(error);
        }
        // Never try to refresh the auth endpoints themselves.
        const authEndpoints = ['/auth/login', '/auth/refresh', '/auth/logout'];
        if (authEndpoints.some(ep => originalRequest.url?.includes(ep))) {
          if (originalRequest.url?.includes('/auth/refresh')) {
            handleAuthFailure();
          }
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise<string | null>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            }
            return Promise.reject(error);
          });
        }

        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            return axiosInstance(originalRequest);
          }
          handleAuthFailure();
          processQueue(new Error('Token refresh failed'), null);
          return Promise.reject(error);
        } catch (refreshError) {
          handleAuthFailure();
          processQueue(refreshError as Error, null);
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      switch (status) {
        case 403:
          toast.error('You do not have permission to perform this action.');
          break;
        case 429:
          toast.error('Too many requests. Please slow down.');
          break;
        case 500:
          toast.error('Server error. Please try again later.');
          break;
        default:
          if (data?.message) toast.error(data.message);
      }
    } else if (error.request) {
      toast.error('No response from server. Please check your connection.');
    } else {
      toast.error('An unexpected error occurred.');
    }

    return Promise.reject(error);
  }
);

/** Typed client — the response interceptor already returns `response.data`. */
export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.get(url, config) as Promise<T>,
  post: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => axiosInstance.post(url, data, config) as Promise<T>,
  put: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => axiosInstance.put(url, data, config) as Promise<T>,
  patch: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => axiosInstance.patch(url, data, config) as Promise<T>,
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.delete(url, config) as Promise<T>,
};

/** Unwrap a success envelope's `data`, throwing on an error envelope. */
export const unwrap = <T>(envelope: ApiEnvelope<T>): T => {
  if (envelope.success) return envelope.data;
  throw new Error('Unexpected API error envelope');
};

/**
 * Extract the backend error `code` (e.g. `PDF_ALREADY_SET`, `CONVERSION_FAILED`,
 * `CONFLICT`) from a rejected request so callers can branch on it. Returns null
 * when the shape is not an API error envelope.
 */
export const getApiErrorCode = (error: unknown): string | null => {
  const axiosErr = error as AxiosError<{
    error?: { code?: string | null };
  }>;
  return axiosErr?.response?.data?.error?.code ?? null;
};

/** HTTP status of a rejected request, or null. */
export const getApiErrorStatus = (error: unknown): number | null => {
  const axiosErr = error as AxiosError;
  return axiosErr?.response?.status ?? null;
};

export default apiClient;
